// Services Page - SEO Optimized Massage Services Directory
"use client";
import React, { useState, useEffect } from "react";
import {
  Search,
  Sparkles,
  Heart,
  MapPin,
  ChevronRight,
  Star,
  Award,
  X
} from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import {
  searchServices,
  getServicesSortedByPopularity
} from "../data/massageServices";
import { useNavigate } from "react-router-dom";

// Generate structured data for SEO
const generateServicesSchema = () => {
  const services = getServicesSortedByPopularity();

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Massage Therapy Services in Edmonton & Calgary, Alberta",
    "description": "Comprehensive list of professional massage therapy services available through certified RMTs in Edmonton and Calgary, Alberta",
    "itemListElement": services.slice(0, 20).map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.name,
        "description": service.description,
        "provider": {
          "@type": "Organization",
          "name": "BookaTherapy"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Edmonton",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "AB",
              "addressCountry": "CA"
            }
          },
          {
            "@type": "City",
            "name": "Calgary",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "AB",
              "addressCountry": "CA"
            }
          }
        ]
      }
    }))
  };
};

// Generate FAQ Schema for common services
const generateFAQSchema = () => {
  const commonServices = getServicesSortedByPopularity().filter(s => s.isCommon).slice(0, 8);

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": commonServices.map(service => ({
      "@type": "Question",
      "name": `What is ${service.name}?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": service.description
      }
    }))
  };
};

export default function Services() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredServices, setFilteredServices] = useState(() => getServicesSortedByPopularity());
  const navigate = useNavigate();

  // Filter services based on search query
  useEffect(() => {
    const results = searchServices(searchQuery);
    setFilteredServices(results);
  }, [searchQuery]);

  // Generate meta description
  const metaDescription = "Explore our comprehensive range of massage therapy services in Edmonton and Calgary, Alberta. From Swedish and Deep Tissue to specialized treatments, find the perfect therapy with certified RMTs.";

  const keywords = "massage therapy Edmonton, massage therapy Calgary, Swedish massage Alberta, deep tissue massage, sports massage, RMT services, therapeutic massage, prenatal massage, hot stone massage, cupping therapy, registered massage therapist";

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Head with Structured Data */}
      <SEOHead
        title="Massage Therapy Services in Edmonton & Calgary, Alberta"
        description={metaDescription}
        keywords={keywords}
        canonical="/services"
        ogTitle="Professional Massage Therapy Services | BookaTherapy"
        ogDescription={metaDescription}
        structuredData={[generateServicesSchema(), generateFAQSchema()]}
      />

      {/* Navigation */}
      <NavbarDemo />

      {/* Simplified Hero Section */}
      <section className="pt-20 sm:pt-24 md:pt-28 pb-10 sm:pb-12 px-4 bg-gradient-to-br from-[#252F4F] to-[#1a2238]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            {/* Location Badge */}
            <Badge className="mb-4 px-4 py-2 text-sm bg-[#98C454] text-[#252F4F] hover:bg-[#87b043]">
              <MapPin className="w-4 h-4 mr-2 inline" />
              Edmonton & Calgary, Alberta
            </Badge>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              Massage Therapy Services
            </h1>

            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto mb-6">
              Browse our complete range of professional massage therapies. All services provided by certified RMTs.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search services... (e.g., Swedish, Deep Tissue, Sports)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-12 py-6 text-base border-0 rounded-lg shadow-lg bg-white"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {searchQuery && (
                <p className="mt-3 text-sm text-gray-300">
                  Found <span className="font-semibold text-white">{filteredServices.length}</span> service{filteredServices.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Award className="w-4 h-4 text-[#98C454]" />
              <span className="text-sm font-medium text-white">Certified RMTs</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Star className="w-4 h-4 text-[#98C454] fill-[#98C454]" />
              <span className="text-sm font-medium text-white">Top-Rated</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <Heart className="w-4 h-4 text-[#98C454]" />
              <span className="text-sm font-medium text-white">100+ Therapists</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Popular Services Header */}
          {!searchQuery && (
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 bg-[#98C454]/20 px-5 py-2 rounded-full mb-3">
                <Sparkles className="w-5 h-5 text-[#98C454]" />
                <span className="font-semibold text-[#252F4F]">Most Popular Services</span>
              </div>
              <p className="text-gray-600">
                Our most requested massage therapies
              </p>
            </div>
          )}

          {/* Services Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service, index) => (
              <Card
                key={`${service.name}-${index}`}
                className={`h-full border-2 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
                  service.isCommon
                    ? 'border-[#98C454]/40 bg-gradient-to-br from-white to-[#98C454]/5'
                    : 'border-gray-200 hover:border-[#98C454]/50'
                }`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <CardTitle className="text-xl font-bold text-[#252F4F] flex-1">
                      {service.name}
                    </CardTitle>
                    {service.isCommon && (
                      <Badge className="bg-[#98C454] text-[#252F4F] ml-2 flex-shrink-0">
                        <Star className="w-3 h-3 mr-1 fill-[#252F4F]" />
                        Popular
                      </Badge>
                    )}
                  </div>
                  <Badge variant="outline" className="w-fit text-xs text-gray-600 border-gray-300">
                    {service.category}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed mb-4">
                    {service.description}
                  </CardDescription>

                  {/* Book Now Button */}
                  <Button
                    onClick={() => navigate('/book')}
                    className="w-full bg-gradient-to-r from-[#98C454] to-[#87b043] hover:from-[#87b043] hover:to-[#98C454] text-[#252F4F] font-semibold group">
                    Book This Service
                    <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {filteredServices.length === 0 && searchQuery && (
            <div className="text-center py-16">
              <div className="mb-6">
                <Search className="w-16 h-16 text-gray-300 mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-[#252F4F] mb-3">
                No services found
              </h3>
              <p className="text-gray-600 mb-6">
                We couldn't find any services matching "{searchQuery}"
              </p>
              <Button
                onClick={() => setSearchQuery("")}
                variant="outline"
                className="border-[#98C454] text-[#252F4F] hover:bg-[#98C454]/10">
                Clear Search
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#252F4F] to-[#1a2238]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Why Choose BookaTherapy?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Alberta's leading massage therapy booking platform
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Award className="w-10 h-10" />,
                title: "Certified Professionals",
                description: "All our therapists are certified RMTs with extensive training"
              },
              {
                icon: <MapPin className="w-10 h-10" />,
                title: "Convenient Locations",
                description: "Services at your home, office, or partner clinics"
              },
              {
                icon: <Heart className="w-10 h-10" />,
                title: "Personalized Care",
                description: "Matching algorithm finds the perfect therapist for you"
              }
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="mb-4 text-[#98C454]">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-300">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#252F4F] mb-4">
            Ready to Experience Professional Massage Therapy?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Book your appointment today and discover the healing power of therapeutic massage
          </p>
          <Button
            onClick={() => navigate('/book')}
            size="lg"
            className="bg-gradient-to-r from-[#98C454] to-[#87b043] hover:from-[#87b043] hover:to-[#98C454] text-[#252F4F] font-bold text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
            Book Your Massage Now
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#252F4F] text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © {new Date().getFullYear()} BookaTherapy. Professional Massage Therapy Services in Edmonton & Calgary, Alberta.
          </p>
        </div>
      </footer>
    </div>
  );
}
